import { Injectable, signal, inject, effect, DOCUMENT } from '@angular/core';

export type Locale = 'fr' | 'en';

export const DEFAULT_LOCALE: Locale = 'fr';

type TranslationDictionary = Record<string, unknown>;

const LOCALE_STORAGE_KEY = 'blisterr.admin.locale';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private readonly doc = inject(DOCUMENT);

  private readonly _locale = signal<Locale>(DEFAULT_LOCALE);
  private readonly _translations = signal<Record<Locale, TranslationDictionary>>({
    fr: {},
    en: {},
  });

  readonly locale = this._locale.asReadonly();
  readonly availableLocales: readonly Locale[] = ['fr', 'en'];

  constructor() {
    // Hydrate la locale depuis localStorage au boot (instantané, sans flash).
    const stored = this.readFromStorage();
    if (stored) {
      this._locale.set(stored);
    }

    // Persiste à chaque changement (effect dédupliqué par le signal).
    effect(() => {
      this.writeToStorage(this._locale());
    });
  }

  loadTranslations(locale: Locale, translations: TranslationDictionary): void {
    this._translations.update(prev => ({
      ...prev,
      [locale]: translations,
    }));
  }

  setLocale(locale: Locale): void {
    if (this.availableLocales.includes(locale)) {
      this._locale.set(locale);
    }
  }

  private readFromStorage(): Locale | null {
    try {
      const raw = this.doc.defaultView?.localStorage.getItem(LOCALE_STORAGE_KEY);
      if (raw === 'fr' || raw === 'en') return raw;
      return null;
    } catch {
      return null;
    }
  }

  private writeToStorage(locale: Locale): void {
    try {
      this.doc.defaultView?.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // localStorage peut throw en mode privé ; on ignore silencieusement.
    }
  }

  t(key: string, params?: Record<string, string | number>): string {
    const translations = this._translations();
    const locale = this._locale();

    let value = this.resolve(translations[locale], key);

    if (value === undefined && locale !== DEFAULT_LOCALE) {
      value = this.resolve(translations[DEFAULT_LOCALE], key);
    }

    if (value === undefined) {
      return key;
    }

    const result = String(value);

    if (!params) {
      return result;
    }

    return this.interpolate(result, params);
  }

  private resolve(dict: TranslationDictionary | undefined, key: string): unknown {
    if (!dict) return undefined;

    const parts = key.split('.');
    let current: unknown = dict;

    for (const part of parts) {
      if (current === null || current === undefined || typeof current !== 'object') {
        return undefined;
      }
      current = (current as Record<string, unknown>)[part];
    }

    return current;
  }

  private interpolate(template: string, params: Record<string, string | number>): string {
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, paramKey: string) => {
      const val = params[paramKey];
      return val !== undefined ? String(val) : `{{${paramKey}}}`;
    });
  }
}
